import { useState } from 'react';
import { NumberInput } from '../ui/NumberInput';
import { btnSecondary } from '../ui/styles';
import { euro } from '../../core/format/currency';
import { fullDate, fromIso, today } from '../../core/format/dates';
import { isKuurExpired, kuurExpiresSoon, kuurRefund, kuurValidUntil, prepaidLeft } from '../../core/rules/kuur';
import type { Course } from '../../core/types/account';
import type { Treatment } from '../../core/types/catalog';
type Props = {
  course: Course;
  treatment: Treatment | undefined;
  /** Geldigheid van een vooruitbetaalde kuur, uit het concept van Instellingen. */
  validMonths: number;
  busy: boolean;
  onSave: (course: Course) => void;
};

/**
 * Betaling van de kuur: vooruit betaald (saldo, geldig tot, wat ze terugkrijgt bij stoppen) of per keer.
 * Tot de kassa er is legt Sarah een vooruitbetaling hier vast; daarna doet de kassa of de webshop dat.
 */
export function CoursePrepaidBlock({
  course,
  treatment,
  validMonths,
  busy,
  onSave
}: Props) {
  const now = today();
  const matchingKuur = treatment?.kuur && treatment.kuur.sessions === course.total ? treatment.kuur.price : undefined;
  const [amount, setAmount] = useState<number | undefined>(matchingKuur);
  const p = course.prepaid;
  if (!p) {
    return <div className="mt-5 border-t border-line pt-4" data-id="element-1093" data-mp="wi3ofo3a5exs">
        <p className="text-[14px] text-ink" data-id="element-1094" data-mp="11gneo0owpcvy">Per keer betaald</p>
        <p className="mt-1 text-[13px] text-muted" data-id="element-1095" data-mp="nq1um8npuwwh">Heeft de klant de hele kuur in één keer betaald? Leg dat vast, dan ziet ze het saldo in Mijn omgeving.</p>
        <div className="mt-3 flex flex-wrap items-end gap-3" data-id="element-1096" data-mp="shjsuoycujdr">
          <label className="block" data-id="element-1097" data-mp="dsdybeagphc7">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-1098" data-mp="dtfaxn74mbw1">Betaald (€)</span>
            <NumberInput decimals value={amount} onChange={setAmount} className="w-28" data-id="element-1099" data-mp="is0adkek0rjf" />
          </label>
          <button type="button" disabled={busy || !amount || amount <= 0} onClick={() => amount && onSave({
          ...course,
          prepaid: {
            paid: amount,
            paidOn: now,
            validUntil: kuurValidUntil(now, validMonths)
          }
        })} className={btnSecondary} data-id="element-1100" data-mp="4sh3sw1e5o98">
            Vooruit betaald vastleggen
          </button>
        </div>
      </div>;
  }
  const expired = isKuurExpired(course, now);
  const left = prepaidLeft(course, now);
  const refund = kuurRefund(course, treatment?.price ?? null, now);
  const soon = kuurExpiresSoon(course, now);
  return <div className="mt-5 border-t border-line pt-4 text-[14px]" data-id="element-1101" data-mp="2ud99nmbe8299">
      <p className="text-ink" data-id="element-1102" data-mp="2pfh4hqlqmjn4">
        Vooruit betaald · {euro(p.paid, {
        cents: true
      })}
        <span className="text-muted" data-id="element-1103" data-mp="2kg4jzcic9onp"> op {fullDate(fromIso(p.paidOn))}</span>
      </p>
      <p className={`mt-1 text-[13px] ${expired ? 'text-clay' : 'text-muted'}`} data-id="element-1104" data-mp="2ki7fud66ac0c">
        {expired ? `Verlopen op ${fullDate(fromIso(p.validUntil))}; de rest is vervallen.` : `Nog ${left} uit de kuur · geldig tot ${fullDate(fromIso(p.validUntil))}`}
      </p>
      {soon !== null && <p className="mt-1 text-[13px] text-clay" data-id="element-1105" data-mp="14y2r9wqc8rq4">
          {soon === 0 ? 'Vandaag is de laatste dag.' : `Verloopt over ${soon === 1 ? '1 dag' : `${soon} dagen`}.`} Ze krijgt (of kreeg) de mail “Kuur verloopt bijna”.
        </p>}
      {!expired && left > 0 && <p className="mt-3 rounded-md bg-sand/60 px-3 py-2 text-[13px] text-ink" data-id="element-1106" data-mp="3pmm8mj09606b">
          Stopt ze nu, dan krijgt ze <span className="font-medium tabular-nums" data-id="element-1107" data-mp="3ufp4ixsi5lpo">{euro(refund.amount, {
          cents: true
        })}</span> terug.{' '}
          <span className="text-muted" data-id="element-1108" data-mp="37jos52tdp2p">{refund.explanation}</span>
        </p>}
    </div>;
}